/**
 * ViewModeContext - Global view mode state for dimmed lights and fullscreen
 * Feature: 057-lightbox-fullscreen-oneup
 *
 * Provides declarative marker components for view mode behavior:
 * - <ViewMode.DimmedFocus> - Elevates content above dimming overlay
 * - <ViewMode.FullscreenTarget> - Marks element to become fullscreen
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode, RefObject } from 'react';
import { cn } from '@/lib/utils';
import logger from '@/utils/logger';

/**
 * View mode types
 * - 'normal': Default view mode
 * - 'dimmedLights': Dim UI elements (header, panels) but keep photos bright
 * - 'fullscreen': Full screen mode with DetailView2 only
 */
export type ViewMode = 'normal' | 'dimmedLights' | 'fullscreen';

/**
 * ViewModeContext value
 */
interface ViewModeContextValue {
  /**
   * Current view mode
   */
  viewMode: ViewMode;

  /**
   * Set the view mode (supports functional updates)
   */
  setViewMode: (mode: ViewMode | ((prev: ViewMode) => ViewMode)) => void;

  /**
   * Check if currently in a specific mode
   */
  isMode: (mode: ViewMode) => boolean;

  /**
   * Register a ref for dimmed lights focus (elevated above overlay)
   * @returns Cleanup function to unregister
   */
  registerDimmedFocus: (ref: RefObject<HTMLElement>) => () => void;

  /**
   * Register a ref as the fullscreen target
   * @returns Cleanup function to unregister
   */
  registerFullscreenTarget: (ref: RefObject<HTMLElement>) => () => void;
}

/**
 * ViewModeContext
 */
const ViewModeContext = createContext<ViewModeContextValue | null>(null);

/**
 * ViewModeProvider props
 */
interface ViewModeProviderProps {
  children: ReactNode;
}

/**
 * ViewModeProvider - Provides view mode state and manages fullscreen behavior
 *
 * Handles:
 * - View mode state management
 * - Registration of dimmed focus and fullscreen target elements
 * - Fullscreen API integration (requestFullscreen, event listeners)
 * - Automatic fullscreen exit on unmount
 *
 * @example
 * ```tsx
 * <ViewModeProvider>
 *   <App />
 * </ViewModeProvider>
 * ```
 */
export function ViewModeProvider({ children }: ViewModeProviderProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('normal');
  const [dimmedFocusRefs] = useState<Set<RefObject<HTMLElement>>>(new Set());
  const fullscreenTargetRef = useRef<RefObject<HTMLElement> | null>(null);

  const isMode = useCallback((mode: ViewMode) => viewMode === mode, [viewMode]);

  // Registration functions
  const registerDimmedFocus = useCallback((ref: RefObject<HTMLElement>) => {
    dimmedFocusRefs.add(ref);
    logger.info('ViewMode', 'Registered dimmed focus element', { count: dimmedFocusRefs.size });

    return () => {
      dimmedFocusRefs.delete(ref);
      logger.info('ViewMode', 'Unregistered dimmed focus element', { count: dimmedFocusRefs.size });
    };
  }, [dimmedFocusRefs]);

  const registerFullscreenTarget = useCallback((ref: RefObject<HTMLElement>) => {
    if (fullscreenTargetRef.current) {
      logger.warn('ViewMode', 'Multiple fullscreen targets registered - last one wins');
    }

    fullscreenTargetRef.current = ref;
    logger.info('ViewMode', 'Registered fullscreen target');

    return () => {
      if (fullscreenTargetRef.current === ref) {
        fullscreenTargetRef.current = null;
        logger.info('ViewMode', 'Unregistered fullscreen target');

        // Exit fullscreen if target unmounts while fullscreen
        if (viewMode === 'fullscreen' && document.fullscreenElement) {
          document.exitFullscreen().catch((error) => {
            logger.error('ViewMode', 'Failed to exit fullscreen on unmount', error);
          });
          setViewMode('normal');
        }
      }
    };
  }, [viewMode]);

  // Effect: Handle entering fullscreen mode
  useEffect(() => {
    if (viewMode === 'fullscreen' && fullscreenTargetRef.current?.current) {
      const element = fullscreenTargetRef.current.current;

      if (!document.fullscreenEnabled) {
        logger.warn('ViewMode', 'Fullscreen API not supported');
        return;
      }

      if (document.fullscreenElement) {
        // Already fullscreen
        return;
      }

      element.requestFullscreen().catch((error) => {
        logger.error('ViewMode', 'Failed to enter fullscreen', error);
        setViewMode('normal'); // Revert on error
      });
    }
  }, [viewMode]);

  // Effect: Handle exiting fullscreen mode
  useEffect(() => {
    if (viewMode !== 'fullscreen' && document.fullscreenElement) {
      document.exitFullscreen().catch((error) => {
        logger.error('ViewMode', 'Failed to exit fullscreen', error);
      });
    }
  }, [viewMode]);

  // Effect: Listen for external fullscreen exits (ESC key, F11, browser button)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;

      // Sync React state with browser state
      if (!isCurrentlyFullscreen && viewMode === 'fullscreen') {
        logger.info('ViewMode', 'Browser exited fullscreen, syncing React state');
        setViewMode('normal');
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [viewMode]);

  const value: ViewModeContextValue = {
    viewMode,
    setViewMode,
    isMode,
    registerDimmedFocus,
    registerFullscreenTarget,
  };

  return (
    <ViewModeContext.Provider value={value}>
      {children}
    </ViewModeContext.Provider>
  );
}

/**
 * useViewMode - Hook to access view mode state
 *
 * @returns ViewModeContextValue
 * @throws Error if used outside ViewModeProvider
 *
 * @example
 * ```tsx
 * const { viewMode, setViewMode, isMode } = useViewMode();
 *
 * // Set mode
 * setViewMode('dimmedLights');
 *
 * // Check mode
 * if (isMode('dimmedLights')) {
 *   // Apply dimming
 * }
 * ```
 */
export function useViewMode(): ViewModeContextValue {
  const context = useContext(ViewModeContext);

  if (!context) {
    throw new Error('useViewMode must be used within ViewModeProvider');
  }

  return context;
}

/**
 * Marker component props
 */
interface ViewModeMarkerProps {
  children: ReactNode;
  className?: string;
}

/**
 * DimmedFocus - Marks content that should appear above dimming overlay
 *
 * When viewMode is 'dimmedLights', this element gets z-20 to appear
 * at full brightness above the dimming overlay (z-10).
 *
 * Automatically renders an exit button when in dimmed lights mode.
 *
 * @example
 * ```tsx
 * <ViewMode.DimmedFocus>
 *   <DetailView2 photos={photos} />
 * </ViewMode.DimmedFocus>
 * ```
 */
export function DimmedFocus({ children, className }: ViewModeMarkerProps) {
  const { viewMode, setViewMode, registerDimmedFocus } = useViewMode();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return registerDimmedFocus(ref);
  }, [registerDimmedFocus]);

  return (
    <div
      ref={ref}
      className={cn(
        // Always maintain height for proper centering
        'h-full',
        // Elevate above dimming overlay when dimmed lights active
        viewMode === 'dimmedLights' && 'relative z-20',
        className
      )}
      data-view-mode-role="dimmed-focus"
    >
      {children}

      {/* Exit button for dimmed lights mode */}
      {viewMode === 'dimmedLights' && (
        <button
          onClick={() => setViewMode('normal')}
          className="fixed top-4 right-4 z-30 px-4 py-2 bg-gray-800 text-white rounded shadow-lg hover:bg-gray-700 transition-colors"
          aria-label="Exit dimmed lights mode"
        >
          Exit dimmed lights
        </button>
      )}
    </div>
  );
}

/**
 * FullscreenTarget - Marks element that should become fullscreen
 *
 * When viewMode is 'fullscreen', the ViewModeProvider calls
 * requestFullscreen() on this element.
 *
 * Only one FullscreenTarget should be active at a time. If multiple
 * are registered, the last one wins (with console warning).
 *
 * Automatically renders an exit button when in fullscreen mode.
 *
 * @example
 * ```tsx
 * <ViewMode.FullscreenTarget>
 *   <main className="h-full">
 *     <DetailView2 photos={photos} />
 *   </main>
 * </ViewMode.FullscreenTarget>
 * ```
 */
export function FullscreenTarget({ children, className }: ViewModeMarkerProps) {
  const { viewMode, setViewMode, registerFullscreenTarget } = useViewMode();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return registerFullscreenTarget(ref);
  }, [registerFullscreenTarget]);

  return (
    <div
      ref={ref}
      className={cn(
        // Full dimensions for fullscreen
        'h-full w-full',
        // Black background when fullscreen
        viewMode === 'fullscreen' && 'bg-black',
        className
      )}
      data-view-mode-role="fullscreen-target"
    >
      {children}

      {/* Exit button for fullscreen mode */}
      {viewMode === 'fullscreen' && (
        <button
          onClick={() => setViewMode('normal')}
          className="fixed top-4 right-4 z-30 px-4 py-2 bg-gray-800 text-white rounded shadow-lg hover:bg-gray-700 transition-colors"
          aria-label="Exit fullscreen mode"
        >
          Exit fullscreen
        </button>
      )}
    </div>
  );
}

/**
 * ViewMode namespace - Contains marker components
 */
export const ViewMode = {
  DimmedFocus,
  FullscreenTarget,
};
